/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.
import exam from '../../../../../share/util/inputValidator';
import alert from "../../../../../share/alert";
import logWrapper from "../../../../../share/logWrapper";
import { GraphMediator } from "../../../../../share/util/GraphLibrary";
const { v4: uuid } = require('uuid');
import apiService from '../../../../../share/util/api/service';
import handler from '../../../../../share/util/apiResultValidator';
import { mapState } from 'vuex/dist/vuex.cjs.js';
import util from '../../../../../share/util/utils';

// コメントの文字数
const COMMENT_MAX_LEN = 500;
const ERR_MSG_KEY = {
  MARKER_SET: "marker_set",
  FETCH: "fetch",
};

export default {
  name: 'markPageWs146',
  props: {
    markerUuid: { type: String, default: "", required: true, },
    userId: { type: String, default: "", required: true, },
    modalMark: { type: Boolean, required: true, },
    loadMark: { type: Number, default: 0, required: true }, // マーカ登録画面の読込判定
    projectId: { type: String, default: "", required: true }, // プロジェクトID
    measuredId: { type: String, default: "", required: true }, // 計測ID
    elapsedTime: { type: Number, default: 0, required: true, },
    baseTime: { type: Number, default: 0, required: true, },
  },

  data() {
    return {
      modalLoading: false,
      isAlert: {},
      comment: "",
      time: "",
      registeredUserId: "",
      isSharedChecked: true,
      timeoutTimer: null
    };
  },

  computed: {
    ...mapState({
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),
  },

  watch: {
    loadMark: {
      handler() {
        this.clearAlert();
        this.getInfo();
      }
    }
  },

  methods: {
    // マーカ情報の保存
    async save() {

      let ret = null;
      // 入力の検証
      if (!this.validate()) return ret;
      // API呼び出し
      const resultResponse = await this.createMeasurementMarker();
      if (resultResponse && resultResponse.uuid) {
        ret = {
          uuid: resultResponse.uuid,
          time: Math.floor(resultResponse.detail.occurred_elapsed_time / 1000),
          comment: resultResponse.description,
          tag: resultResponse.tag
        };
      }
      else if (resultResponse && resultResponse.error) {
        ret = { error: resultResponse.error };
      }
      return ret;
    },

    // マーカ情報のクリア
    async clear() {
      this.comment = "";
      this.time = "";

      // API呼び出し
      return await this.deleteMeasurementMarker();
    },

    validate() {
      // 検証結果キャッシュの初期化
      this.clearAlert();
      const resultPool = [];

      // メモの文字数チェック
      this.isAlert.comment = exam.excuteValidateCommons(this.comment, false, 0, COMMENT_MAX_LEN);
      resultPool.push(this.isAlert.comment.valid);

      const faults = resultPool.filter(el => el === false);
      return faults.length === 0;
    },

    // 入力検証アラートのクリア
    clearAlert: function () {
      this.isAlert = {
        comment: { valid: null, msg: '' },
      };
    },

    // マーカ情報更新
    async update() {

      let ret = null;
      // 入力の検証
      if (!this.validate()) return ret;
      // API呼び出し
      const resultResponse = await this.replaceMeasurementMarker(this.isSharedChecked);
      if (resultResponse && resultResponse.uuid) {
        ret = {
          uuid: resultResponse.uuid,
          time: Math.floor(resultResponse.detail.occurred_elapsed_time / 1000),
          comment: resultResponse.description,
          tag: resultResponse.tag
        };
      }
      else if (resultResponse && resultResponse.error) {
        ret = { error: resultResponse.error };
      }
      return ret;
    },

    // マーカ情報取得
    async getInfo() {
      // 登録と更新の判定（markerUuidが存在する場合は更新）
      if (this.markerUuid !== "") {

        // API呼び出し
        await this.getMeasurementMarker();
      } else {
        this.comment = "";
        this.time = GraphMediator.getPassedTimeStr(this.elapsedTime, true);
        this.isSharedChecked = true;
        this.registeredUserId = "";
      }
    },

    /**
     * 指定した計測UUIDに紐づけて、計測マーカーを作成する
     * @returns PostのResponse
     */
    async createMeasurementMarker() {
      try {
        if (this.modalMark) {
          await this.setModalLoading(true);
        }
        const response = await this.registerGraphMarker(false);

        // response が不正の場合、マーカ設定エラーを表示
        if (response?.error) this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);

        return response;
      } catch (e) {
        logWrapper.log(`createMeasurementMarker error: ${e}`);
        return {
          error: "CreateMarkerError",
        };
      } finally {
        await this.setModalLoading(false);
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
          this.timeoutTimer = null;
        }
      }
    },


    /**
     * (WS.146) マーカーを登録する
     * @returns {Object} マーカー情報
     */
    async registerGraphMarker(isUpdate = false) {
      const markerId = this.markerUuid || uuid();
      const endPoint = '/projects/' + this.projectId + '/' + this.measuredId + '/markers/' + markerId;
      const registerUserId = isUpdate ? this.registeredUserId : this.userId;

      const body = {
        marker_time: this.elapsedTime + this.baseTime,
        comment: this.comment,
        is_shared: this.isSharedChecked,
        user_id: registerUserId,
        is_update: isUpdate,
        region_info: this.cachedProjectDataSource
      };
      // マーカー登録のAPIとして呼び出す
      const response = await apiService.postCall(apiService.WSP, endPoint, body);

      let ret = null;
      // APIレスポンスの検証
      // レスポンス不正時はマーカのエラーダイアログを表示するため isAlert は false にする
      handler.validate(handler.validateTypes.all, response, this, null, () => {
        // 正常応答コールバック
        ret = {
          uuid: markerId,
          detail: {
            occurred_elapsed_time: Math.floor(this.elapsedTime * 1000) // マイクロ秒
          },
          description: this.comment,
          tag: { userId: registerUserId, shared: String(this.isSharedChecked) }
        };
      }, (result) => {
        // レスポンス不正時のコールバック
        // エラー内容に関わらず error を返す
        ret = {
          error: "MarkerError",
        };
        logWrapper.log(
          "receive invalid response. continue.",
          result,
          true
        );
      }, null, false);

      return ret;
    },

    /**
     * (WS.146) マーカーを削除する
     * @returns {Object} マーカー情報
     */
    async deleteGraphMarker() {
      const endPoint = '/projects/' + this.projectId + '/' + this.measuredId + '/markers/' + this.markerUuid + '?region_info=' + this.cachedProjectDataSource;

      // マーカー削除のAPIを呼び出す
      const response = await apiService.deleteCall(apiService.WSP, endPoint);

      let ret = null;
      // APIレスポンスの検証
      // レスポンス不正時はマーカのエラーダイアログを表示するため isAlert は false にする
      handler.validate(handler.validateTypes.all, response, this, null, () => {
        // 正常応答コールバック
        ret = {
          is_success: true
        };
      }, (result) => {
        // レスポンス不正時のコールバック
        // エラー内容に関わらず error を返す
        ret = {
          error: "MarkerError",
        };
        logWrapper.log(
          "receive invalid response. continue.",
          result,
          true
        );
      }, null, false);

      return ret;
    },

    /**
     * (WS.146) マーカー情報を取得する
     * @returns {Object} マーカー情報
     */
    async getGraphMarker() {
      const endPoint = '/projects/' + this.projectId + '/' + this.measuredId + '/markers/' + this.markerUuid;

      // マーカー削除のAPIを呼び出す
      const params = { region_info: this.cachedProjectDataSource };
      const response = await apiService.getParamCall(apiService.WSP, endPoint, params, null);

      let ret = null;
      // APIレスポンスの検証
      // レスポンス不正時はマーカのエラーダイアログを表示するため isAlert は false にする
      handler.validate(handler.validateTypes.all, response, this, null, () => {
        // 正常応答コールバック
        const markerInfo = response.data;
        ret = {
          uuid: markerInfo.marker_id,
          detail: {
            occurred_elapsed_time: Math.floor((markerInfo.marker_time - this.baseTime) * 1000) // マイクロ秒
          },
          description: markerInfo.comment,
          tag: { userId: markerInfo.user_id, shared: markerInfo.is_shared }
        };
      }, (result) => {
        // レスポンス不正時のコールバック
        // エラー内容に関わらず error を返す
        ret = {
          error: "MarkerError",
        };
        logWrapper.log(
          "receive invalid response. continue.",
          result,
          true
        );
      }, null, false);

      return ret;
    },

    /**
     * 指定した計測マーカーの情報を更新する
     * @returns PutのResponses
     */
    async replaceMeasurementMarker() {
      try {
        await this.setModalLoading(true);
        return await this.registerGraphMarker(true);
      } finally {
        await this.setModalLoading(false);
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
          this.timeoutTimer = null;
        }
      }
    },

    /**
     * 指定した計測マーカーを削除する
     */
    async deleteMeasurementMarker() {
      try {
        await this.setModalLoading(true);
        const response = await this.deleteGraphMarker();
        if (response?.error) this.showUnexpectedError(ERR_MSG_KEY.MARKER_SET);
        return response;
      } catch (e) {
        logWrapper.log(`deleteMeasurementMarker error: ${e}`);
        return {
          error: "DeleteMarkerError",
        };
      } finally {
        await this.setModalLoading(false);
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
          this.timeoutTimer = null;
        }
      }
    },

    /**
     * 指定した計測マーカーの情報を取得する
     * @returns GetのResponse
     */
    async getMeasurementMarker() {
      try {
        await this.setModalLoading(true);

        const resultResponse = await this.getGraphMarker();

        // 必要なデータをthisに設定
        if (resultResponse && resultResponse.uuid) {
          this.comment = resultResponse.description;
          this.time = GraphMediator.getPassedTimeStr(Math.floor(resultResponse.detail.occurred_elapsed_time / 1000), true);
          this.registeredUserId = resultResponse.tag && resultResponse.tag.userId;
          this.isSharedChecked = resultResponse.tag && resultResponse.tag.shared;
        }

        return resultResponse;
      } catch (e) {
        logWrapper.log(`getMeasurementMarker error: ${e}`);
        return {
          error: "GetMarkerError",
        };
      } finally {
        // スピナー消去後、削除/保存ボタン活性化までにタイムラグを発生させる（_leaveCbエラー回避のため）
        await this.setModalLoading(false , 100);
        if (this.timeoutTimer) {
          clearTimeout(this.timeoutTimer);
          this.timeoutTimer = null;
        }
      }
    },

    /**
     * マーカ操作時のエラー
     */
    showUnexpectedError(action) {
      let messageKey = "";
      switch (action) {
        case ERR_MSG_KEY.FETCH:
          // メッセージは同一とする
          messageKey = "individual.error_data_monitor_marker_set";
          break;
        default:
          messageKey = "individual.error_data_monitor_marker_set";
          break;
      }
      alert.showError(this, null, this.$t(messageKey), () => { });
    },

    /**
     * モーダルのローディング状態を設定する
     * @param {Boolean} bool ローディング状態
     * @param {Number} delay ローディング状態の変更後に待機する時間 (ms)
     */
    async setModalLoading(bool, delay = 0) {
      this.modalLoading = bool;
      if (delay > 0) {
        await util.sleep(delay);
      }
      // モーダルのローディング状態を親コンポーネントに通知する 
      this.$emit("set-mark-modal-loading", this.modalLoading);
    }
  },

  async created() {
    // noop
  },

  mounted() {
    // noop
  },
};